// src/pages/chef/ChefDashboard.tsx
import React, { useEffect, useState, lazy, Suspense } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useUserStore } from "../../store/user.store";
import { db } from "../../lib/firebase";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  updateDoc,
} from "firebase/firestore";
import { Spinner } from "../../components/common/Spinner";
import { Card } from "../../components/common/Card";
import { Button } from "../../components/common/Button";
import { toast } from "react-hot-toast";
import { Kitchen } from "../../types/index";
import ManageMenu from "./ManageMenu";
import { KitchenOrderManager } from "../../components/kitchen/KitchenOrderManager";

const ManageSpecials = lazy(() => import("./ManageSpecials"));
const PasteAnnouncement = lazy(() => import("../../components/announce/PasteAnnouncement"));

const sections = [
  { key: "orders", label: "Orders" },
  { key: "announce", label: "📋 Paste" },
  { key: "menu", label: "Menu" },
  { key: "specials", label: "Specials" },
];

const isKitchenOnline = (status?: string) => {
  const s = (status || "").toLowerCase();
  return s === "open" || s === "active";
};

const getKitchensForChef = async (
  chefUid?: string,
  isAdmin?: boolean
): Promise<Kitchen[]> => {
  if (!chefUid) return [];
  const kitchensRef = collection(db, "kitchens");

  if (isAdmin) {
    const snap = await getDocs(kitchensRef);
    return snap.docs.map(
      (d) => ({ uid: d.id, id: d.id, ...d.data() } as unknown as Kitchen)
    );
  }

  const kitchensQuery = query(
    kitchensRef,
    where("chefUids", "array-contains", chefUid)
  );
  const kitchenSnap = await getDocs(kitchensQuery);
  return kitchenSnap.docs.map(
    (d) => ({ uid: d.id, id: d.id, ...d.data() } as unknown as Kitchen)
  );
};

const ChefDashboard: React.FC = () => {
  const { userProfile } = useUserStore();
  const chefUid = userProfile?.uid;
  const isAdmin =
    userProfile?.currentRole === "admin" ||
    userProfile?.roles?.includes("admin");
  const queryClient = useQueryClient();

  const [selectedKitchenId, setSelectedKitchenId] = useState<string | null>(
    null
  );
  const [selectedTab, setSelectedTab] = useState("orders");

  const { data: kitchens = [], isLoading } = useQuery(
    ["chefKitchens", chefUid, isAdmin],
    () => getKitchensForChef(chefUid, !!isAdmin),
    { enabled: !!chefUid }
  );

  // Prefer open kitchens in the list for chefs
  const visibleKitchens = isAdmin
    ? kitchens.filter((k) => {
        const s = (k.status || "").toLowerCase();
        return s !== "blocked";
      })
    : kitchens;

  useEffect(() => {
    if (visibleKitchens.length > 0 && !selectedKitchenId) {
      setSelectedKitchenId(
        visibleKitchens[0].uid || visibleKitchens[0].id || ""
      );
    }
  }, [visibleKitchens, selectedKitchenId]);

  const selectedKitchen = visibleKitchens.find(
    (k) => k.uid === selectedKitchenId || k.id === selectedKitchenId
  );

  const online = isKitchenOnline(selectedKitchen?.status);

  const { mutate: updateStatus, isLoading: isUpdatingStatus } = useMutation(
    async ({
      kitchenId,
      nextOnline,
    }: {
      kitchenId: string;
      nextOnline: boolean;
    }) => {
      // Match Admin vocabulary: active / paused (also accepted as open by the site)
      await updateDoc(doc(db, "kitchens", kitchenId), {
        status: nextOnline ? "active" : "paused",
      });
      return nextOnline;
    },
    {
      onSuccess: (nextOnline) => {
        toast.success(nextOnline ? "Kitchen is online" : "Kitchen paused");
        queryClient.invalidateQueries(["chefKitchens", chefUid]);
      },
    }
  );

  if (isLoading)
    return (
      <div className="flex justify-center p-20">
        <Spinner />
      </div>
    );

  if (!visibleKitchens.length) {
    return (
      <div className="max-w-lg mx-auto px-4 py-16 text-center">
        <p className="text-lg font-bold text-gray-800 mb-2">No kitchens linked</p>
        <p className="text-sm text-gray-500">
          Ask admin to add your account to the kitchen’s chef list.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto py-4 sm:py-8 px-3 sm:px-4 pb-28">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
          Chef dashboard
        </h1>
        {visibleKitchens.length > 1 && (
          <select
            className="border border-gray-200 p-3 rounded-xl bg-white shadow-sm text-sm font-semibold w-full sm:w-auto min-h-[44px]"
            value={selectedKitchenId || ""}
            onChange={(e) => setSelectedKitchenId(e.target.value)}
          >
            {visibleKitchens.map((k) => (
              <option key={k.id} value={k.id}>
                {k.kitchen_name || k.name || k.id}
                {!isKitchenOnline(k.status) ? " (paused)" : ""}
              </option>
            ))}
          </select>
        )}
      </div>

      {selectedKitchen && (
        <>
          <Card className="mb-4 p-4 sm:p-5 border-l-4 border-indigo-500">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
              <div className="min-w-0">
                <h2 className="font-bold text-lg sm:text-xl truncate">
                  {selectedKitchen.kitchen_name || selectedKitchen.name}
                </h2>
                <p className="text-sm text-gray-500 break-words">
                  {selectedKitchen.address || "No address"}
                </p>
              </div>
              <div className="flex items-center justify-between sm:justify-end gap-3">
                <span
                  className={`text-sm font-bold uppercase ${
                    online ? "text-green-600" : "text-red-500"
                  }`}
                >
                  {online ? "Online" : "Paused"}
                </span>
                <button
                  type="button"
                  aria-label="Toggle kitchen online"
                  onClick={() =>
                    updateStatus({
                      kitchenId: selectedKitchen.id!,
                      nextOnline: !online,
                    })
                  }
                  disabled={isUpdatingStatus}
                  className={`relative inline-flex items-center h-8 rounded-full w-14 transition-colors ${
                    online ? "bg-green-500" : "bg-gray-300"
                  }`}
                >
                  <span
                    className={`inline-block w-6 h-6 transform bg-white rounded-full shadow transition-transform ${
                      online ? "translate-x-7" : "translate-x-1"
                    }`}
                  />
                </button>
              </div>
            </div>
          </Card>

          <div className="flex gap-1 mb-4 bg-gray-100 p-1 rounded-xl overflow-x-auto">
            {sections.map((sec) => (
              <button
                key={sec.key}
                type="button"
                onClick={() => setSelectedTab(sec.key)}
                className={`flex-1 min-w-[5.5rem] py-2.5 text-sm font-bold rounded-lg transition-all min-h-[44px] ${
                  selectedTab === sec.key
                    ? "bg-white shadow text-indigo-600"
                    : "text-gray-500"
                }`}
              >
                {sec.label}
              </button>
            ))}
          </div>

          <Suspense
            fallback={
              <div className="p-10 text-center">
                <Spinner />
              </div>
            }
          >
            {selectedTab === "orders" && (
              <KitchenOrderManager kitchenId={selectedKitchen.id!} />
            )}
            {selectedTab === "menu" && (
              <ManageMenu
                kitchenId={selectedKitchen.id!}
                chefUid={chefUid!}
                kitchenName={
                  selectedKitchen.kitchen_name || selectedKitchen.name || ""
                }
              />
            )}
            {selectedTab === "announce" && (
              <PasteAnnouncement kitchenId={selectedKitchen.id!} />
            )}
            {selectedTab === "specials" && (
              <ManageSpecials
                kitchenId={selectedKitchen.id!}
                kitchenName={
                  selectedKitchen.kitchen_name || selectedKitchen.name || ""
                }
              />
            )}
          </Suspense>
        </>
      )}
    </div>
  );
};

export default ChefDashboard;
